import moment from 'moment'
import * as React from 'react'
import FlexView from '@/modules/shared/components/FlexView'

import { VehicleCalendarModel } from '@/app/models/vehicle/calendar/vehicleCalendarModel'
import { VehicleCalendarDayModel } from '@/app/models/vehicle/calendar/vehicleCalendarDayModel'

import { CalendarOption } from '@/app/constants/calendarOptions'
import { DATE_FORMAT } from '@/app/constants/date'

import {
  VehicleCalendarDayItem,
  CalendarMenuAction,
} from './VehicleCalendarDayItem'
import { CalendarMonths } from '@/modules/drivers/tabs/activities/components/charts/yearly/CalendarMonths'
import { CalendarHeaderItem } from '@/modules/drivers/tabs/activities/components/charts/yearly/CalendarHeaderItem'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { LoaderIcon } from '@/app/features/icons/Loader'
import { ActivitiesEmptyState } from '@/modules/shared/components/ActivitiesEmptyState'
import { VehicleDayContextMenu } from './VehicleDayContextMenu'
import { createPortal } from 'react-dom'

export type VehicleCalendarProps = {
  companyId: string
  vehicleId?: number
  calendar: VehicleCalendarModel
  selectedOptions?: Array<CalendarOption>
  isLoading: boolean
  onSelected?: (day: VehicleCalendarDayModel) => void
  onCalendarAction?: (
    day: VehicleCalendarDayModel,
    action: CalendarMenuAction,
    data?: any,
  ) => void
  selectedDays?: Array<VehicleCalendarDayModel>
  onMultiSelected?: (days: Array<VehicleCalendarDayModel>) => void
  changedDay?: VehicleCalendarDayModel
  refreshCalendar?: () => Promise<void>
}

const VehicleYearlyActivityCalendar: React.FC<VehicleCalendarProps> = (
  props,
) => {
  const {
    companyId,
    vehicleId,
    calendar,
    selectedOptions,
    onSelected,
    onCalendarAction,
    selectedDays: propsSelectedDays,
    onMultiSelected,
    changedDay,
    refreshCalendar,
    isLoading,
  } = props
  const { t } = useLocalize()
  const [selectedDay, setSelectedDay] =
    React.useState<VehicleCalendarDayModel | null>(null)
  const [selectedDays, setSelectedDays] = React.useState<
    Array<VehicleCalendarDayModel>
  >([])
  const [selectedDates, setSelectedDates] = React.useState<Array<Date>>([])
  const [days, setDays] = React.useState<Array<Array<VehicleCalendarDayModel>>>(
    calendar != null ? calendar.days : [],
  )

  const isDraggingRef = React.useRef(false)
  const dragStartDayRef = React.useRef<string | null>(null)
  const dragRangeRef = React.useRef<[number, number] | null>(null)

  const calendarRef = React.useRef<HTMLDivElement>(null)

  const [contextMenu, setContextMenu] = React.useState<{
    day: VehicleCalendarDayModel
    anchor: { x: number; y: number }
    isOpen: boolean
  } | null>(null)

  const handleContextMenuRequest = React.useCallback(
    (day: VehicleCalendarDayModel, anchor: { x: number; y: number }) => {
      setContextMenu({ day, anchor, isOpen: true })
    },
    [],
  )

  const handleContextMenuClose = React.useCallback(() => {
    setContextMenu((prev) => (prev ? { ...prev, isOpen: false } : null))
  }, [])

  const [containerWidth, setContainerWidth] = React.useState(0)

  React.useEffect(() => {
    const el = calendarRef.current
    if (!el) return undefined
    const observer = new ResizeObserver((entries) => {
      setContainerWidth(entries[0].contentRect.width)
    })
    observer.observe(el)
    setContainerWidth(el.clientWidth)
    return () => observer.disconnect()
  }, [])

  const flatDays = React.useMemo(() => {
    if (!days) return []
    return days
      .flat()
      .filter(
        (d): d is VehicleCalendarDayModel => d != null && d.dayNumber !== 0,
      )
  }, [days])

  const dayIndexMap = React.useMemo(() => {
    const map = new Map<string, number>()
    flatDays.forEach((d, i) => {
      map.set(moment(d.activityDayLocalTimeDate).format(DATE_FORMAT), i)
    })
    return map
  }, [flatDays])

  React.useEffect(() => {
    const handleMouseUp = () => {
      if (isDraggingRef.current && dragRangeRef.current) {
        const [min, max] = dragRangeRef.current
        const rangeDays = flatDays.slice(min, max + 1)
        const rangeDates = rangeDays.map((d) =>
          moment(d.activityDayLocalTimeDate).toDate(),
        )
        setSelectedDays(rangeDays)
        setSelectedDates(rangeDates)
        onMultiSelected?.(rangeDays)
      }
      isDraggingRef.current = false
      dragRangeRef.current = null
    }
    window.addEventListener('mouseup', handleMouseUp)
    return () => window.removeEventListener('mouseup', handleMouseUp)
  }, [flatDays, onMultiSelected])

  const handleDayMouseDown = React.useCallback(
    (day: VehicleCalendarDayModel, e: React.MouseEvent) => {
      if (e.button !== 0) return
      isDraggingRef.current = true
      dragStartDayRef.current = moment(day.activityDayLocalTimeDate).format(
        DATE_FORMAT,
      )
    },
    [],
  )

  const handleDayMouseEnter = React.useCallback(
    (day: VehicleCalendarDayModel) => {
      if (!isDraggingRef.current || !dragStartDayRef.current) return
      const startIdx = dayIndexMap.get(dragStartDayRef.current)
      const endIdx = dayIndexMap.get(
        moment(day.activityDayLocalTimeDate).format(DATE_FORMAT),
      )
      if (startIdx == null || endIdx == null) return
      const min = Math.min(startIdx, endIdx)
      const max = Math.max(startIdx, endIdx)
      dragRangeRef.current = [min, max]

      const newSet = new Set<string>()
      for (let i = min; i <= max; i++) {
        newSet.add(
          moment(flatDays[i].activityDayLocalTimeDate).format(DATE_FORMAT),
        )
      }
      setSelectedDaysSet(newSet)
    },
    [flatDays, dayIndexMap],
  )

  React.useEffect(() => {
    if (calendar != null) {
      setDays(calendar.days)
    }
  }, [calendar])

  React.useEffect(() => {
    if (propsSelectedDays != null) {
      setSelectedDays(propsSelectedDays)
    }
  }, [propsSelectedDays])

  const [selectedDaysSet, setSelectedDaysSet] = React.useState<Set<string>>(
    () => new Set(),
  )

  React.useEffect(() => {
    if (isDraggingRef.current) return
    setSelectedDaysSet(
      new Set(
        selectedDays.map((d) =>
          moment(d.activityDayLocalTimeDate).format(DATE_FORMAT),
        ),
      ),
    )
  }, [selectedDays])

  const dateRange = (startDate: Date, endDate: Date) => {
    let currentTime = moment(startDate)
    const endTime = moment(endDate)
    const result: Date[] = []
    while (currentTime <= endTime) {
      result.push(currentTime.toDate())
      currentTime = currentTime.clone().add(1, 'days')
    }
    return result
  }

  const prepareHeader = (): Array<number> => {
    const header: Array<number> = []
    if (days && days.length > 0 && days[0]) {
      const daysCount = days[0].length
      for (let i = 0; i < daysCount; i++) {
        header.push(i % 7)
      }
    }
    return header
  }

  const renderWeekDayHeaders = (width: number) => {
    const header = prepareHeader()
    return (
      <FlexView
        height={30}
        hAlignContent="left"
        vAlignContent="center"
        style={{ border: 'solid', borderWidth: 0 }}
      >
        {header.map((item, index) => (
          <CalendarHeaderItem
            key={index}
            dayOfWeek={item}
            height={32}
            width={width}
            translate={t}
          />
        ))}
      </FlexView>
    )
  }

  const renderMonths = (height: number, width: number) => {
    return (
      <div className="st-sticky tw-z-20">
        <div
          style={{
            width: width,
            height: 30,
            borderStyle: 'solid',
            borderWidth: 0,
            borderRadius: 0,
            background: '#fffff',
          }}
        />
        <CalendarMonths height={height} width={width} translate={t} />
      </div>
    )
  }

  const handleDaySelection = React.useCallback(
    (daySelected: VehicleCalendarDayModel, e: MouseEvent) => {
      if ((e.target as any).className.indexOf('react-contextmenu-item') !== -1)
        return

      const isLeftClick = e.button === 0
      if (!isLeftClick) return

      const isMultiSelectKey = e.metaKey || e.ctrlKey

      if (isMultiSelectKey || e.shiftKey) {
        let newSelectedDates: Date[] = []

        if (e.shiftKey) {
          if (selectedDays.length > 0) {
            const firstDay = new Date(Math.min.apply(null, selectedDates))
            const selectedDate = moment(
              daySelected.activityDayLocalTimeDate,
            ).toDate()

            const range =
              selectedDate > firstDay
                ? dateRange(firstDay, selectedDate)
                : dateRange(selectedDate, firstDay)

            newSelectedDates = [
              ...selectedDates.filter(
                (sd) =>
                  !range.some(
                    (r) =>
                      moment(r).format(DATE_FORMAT) ===
                      moment(sd).format(DATE_FORMAT),
                  ),
              ),
              ...range,
            ]
          } else {
            newSelectedDates = [
              moment(daySelected.activityDayLocalTimeDate).toDate(),
            ]
          }
        } else {
          const dayDateFormatted = moment(
            daySelected.activityDayLocalTimeDate,
          ).format(DATE_FORMAT)

          if (!selectedDaysSet.has(dayDateFormatted)) {
            newSelectedDates = [
              ...selectedDates,
              moment(daySelected.activityDayLocalTimeDate).toDate(),
            ]
          } else {
            newSelectedDates = selectedDates.filter(
              (d) => moment(d).format(DATE_FORMAT) !== dayDateFormatted,
            )
          }
        }

        const selectedDatesSet = new Set(
          newSelectedDates.map((d) => moment(d).format(DATE_FORMAT)),
        )

        const allSelectedDays: VehicleCalendarDayModel[] = []
        days.forEach((rows) => {
          rows.forEach((d) => {
            if (
              selectedDatesSet.has(
                moment(d.activityDayLocalTimeDate).format(DATE_FORMAT),
              )
            ) {
              allSelectedDays.push(d)
            }
          })
        })

        setSelectedDates(newSelectedDates)
        setSelectedDays(allSelectedDays)
        onSelected && onSelected(daySelected)
        onMultiSelected && onMultiSelected(allSelectedDays)
      } else {
        const isSameDay =
          selectedDay &&
          moment(selectedDay.activityDayLocalTimeDate).format(DATE_FORMAT) ===
            moment(daySelected.activityDayLocalTimeDate).format(DATE_FORMAT)

        if (isSameDay) {
          setSelectedDay(null)
          setSelectedDays([])
          setSelectedDates([])
        } else {
          setSelectedDay(daySelected)
          setSelectedDays([daySelected])
          setSelectedDates([
            moment(daySelected.activityDayLocalTimeDate).toDate(),
          ])
        }

        onSelected && onSelected(daySelected)
        onMultiSelected && onMultiSelected(isSameDay ? [] : [daySelected])
      }
    },
    [
      selectedDaysSet,
      selectedDates,
      selectedDays,
      days,
      onSelected,
      onMultiSelected,
    ],
  )

  const selectedDayFormatted = React.useMemo(
    () =>
      selectedDay
        ? moment(selectedDay.activityDayLocalTimeDate).format(DATE_FORMAT)
        : null,
    [selectedDay],
  )

  const renderCalendarBody = (
    daysData: VehicleCalendarDayModel[][],
    height: number,
    width: number,
  ) => {
    return (
      <FlexView
        column
        hAlignContent="left"
        vAlignContent="center"
        style={{ border: 'solid', borderWidth: 0 }}
      >
        {daysData &&
          daysData.map((item, itemIndex) => (
            <FlexView
              key={itemIndex}
              height={height}
              hAlignContent="left"
              vAlignContent="center"
            >
              {item.map((day: VehicleCalendarDayModel, dayIndex) => {
                const dayDateFormatted = moment(
                  day.activityDayLocalTimeDate,
                ).format(DATE_FORMAT)
                const isSelectedDay = selectedDayFormatted === dayDateFormatted
                const isDayMultiSelected = selectedDaysSet.has(dayDateFormatted)

                return (
                  <VehicleCalendarDayItem
                    companyId={companyId}
                    translate={t}
                    onSelected={handleDaySelection}
                    onMouseDown={handleDayMouseDown}
                    onMouseEnterDay={handleDayMouseEnter}
                    onContextMenuRequest={handleContextMenuRequest}
                    isContextMenuOpen={contextMenu?.isOpen === true}
                    isSelected={isSelectedDay}
                    isMultiSelected={isDayMultiSelected}
                    selectedOptions={selectedOptions}
                    key={dayIndex}
                    day={day}
                    height={height}
                    width={width}

                    isFirstRow={itemIndex === 0}
                    isLastInRow={dayIndex === item.length - 1}
                  />
                )
              })}
            </FlexView>
          ))}
      </FlexView>
    )
  }

  const HEIGHT = 63
  const MONTH_COLUMN_WIDTH = 36
  const MIN_DAY_WIDTH = 36
  const numberOfColumns = days?.[0]?.length || 1
  const WIDTH =
    containerWidth > 0
      ? Math.max(
          MIN_DAY_WIDTH,
          Math.floor((containerWidth - MONTH_COLUMN_WIDTH) / numberOfColumns),
        )
      : MIN_DAY_WIDTH

  return (
    <div ref={calendarRef} className="tw-w-full tw-overflow-auto">
      {isLoading && (
        <div className="tw-grid tw-h-[500px] tw-w-full tw-place-items-center">
          <div className="tw-flex tw-flex-col tw-items-center tw-gap-4">
            <LoaderIcon className="tw-h-16 tw-w-16 tw-animate-spin tw-text-primary" />
            <span className="tw-text-gray-600">
              {t('driverModule.loading-calendar')}
            </span>
          </div>
        </div>
      )}
      {!isLoading && days && days.length > 0 && (
        <div className="tw-flex tw-min-w-fit tw-overflow-auto">
          {renderMonths(HEIGHT, 36)}
          <div className="tw-flex tw-flex-col">
            {renderWeekDayHeaders(WIDTH)}
            <div>{renderCalendarBody(days, HEIGHT, WIDTH)}</div>
          </div>
        </div>
      )}
      {!isLoading && (!days || days.length === 0) && (
        <div className="tw-grid tw-min-h-[min(60vh,560px)] tw-w-full tw-place-items-center tw-px-6 tw-pb-12 tw-pt-[min(23vh,9rem)]">
          <ActivitiesEmptyState
            title={t('dailyActivities.empty-activities-title')}
            subtitle={t('dailyActivities.empty-activities-subtitle')}
          />
        </div>
      )}
      {contextMenu &&
        createPortal(
          <VehicleDayContextMenu
            key={String(contextMenu.day.activityDayLocalTimeDate)}
            vehicleId={vehicleId}

            companyId={companyId}
            day={contextMenu.day as any}
            translate={t}
            onCalendarAction={onCalendarAction as any}
            refreshCalendar={refreshCalendar}
            anchorPoint={contextMenu.anchor}
            isOpen={contextMenu.isOpen}
            onClose={handleContextMenuClose}
            selectedDays={selectedDays as any}
            currentSelectedDay={selectedDay as any}
            setSelectedDay={setSelectedDay as any}
          />,
          document.body,
        )}
    </div>
  )
}

export default VehicleYearlyActivityCalendar
